import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Shield, ChevronRight, Undo2 } from "lucide-react";

const ACTION_LABELS = {
  "settings.business.update": "Config attività modificata",
  "settings.financial.update": "Parametri economici modificati",
  "settings.crm.update": "Regole CRM modificate",
  "settings.list.update": "Lista opzioni modificata",
  "settings.business.restore": "Config attività ripristinata",
  "settings.financial.restore": "Parametri economici ripristinati",
  "settings.crm.restore": "Regole CRM ripristinate",
  "settings.list.restore": "Lista opzioni ripristinata",
  "user.create": "Utente creato",
  "user.update": "Utente modificato",
  "user.delete": "Utente eliminato",
  "account.create": "Conto creato",
  "account.update": "Conto modificato",
  "account.delete": "Conto eliminato",
};

const RESTORABLE = new Set(["settings.list.update", "settings.business.update", "settings.financial.update", "settings.crm.update"]);

function fmtVal(v) {
  if (v == null) return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}

export default function Audit() {
  const { user, can } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  // FASE 7.4 STEP E — permission-based access + restore visibility.
  const canRead = can("audit.read");
  const canRestore = can("audit.restore");

  useEffect(() => {
    if (!canRead) return;
    loadRows();
  }, [canRead]);

  const loadRows = async () => {
    const { data } = await api.get("/audit?limit=500");
    setRows(data);
  };

  const restore = async (row) => {
    if (!window.confirm(`Ripristinare "${ACTION_LABELS[row.action] || row.action}" al valore precedente?`)) return;
    try {
      await api.post(`/audit/${row.id}/restore`);
      toast.success("Valore ripristinato");
      setSelected(null);
      loadRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore ripristino");
    }
  };

  const filtered = useMemo(() => rows.filter((r) => {
    if (actionFilter !== "all" && !r.action?.startsWith(actionFilter)) return false;
    if (q) {
      const hay = `${r.user_email} ${r.action} ${r.entity || ""} ${r.note || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [rows, q, actionFilter]);

  if (user && !canRead) {
    return (
      <div className="border border-red-300 bg-red-50 rounded-lg p-8 text-center" data-testid="audit-no-access">
        <Shield size={28} className="mx-auto text-red-700 mb-3" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl">Accesso riservato</h2>
        <p className="text-sm text-red-800 mt-2">Non hai i permessi per visualizzare l'audit log.</p>
      </div>
    );
  }

  return (
    <div data-testid="audit-page">
      <PageHeader eyebrow="Compliance" title="Audit Log"
        subtitle="Storia completa delle modifiche amministrative con utente, timestamp e dettaglio delle variazioni." />

      <div className="flex gap-3 mb-4">
        <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca utente, azione, entità…" className="max-w-xs" />
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="max-w-xs" data-testid="audit-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le azioni</SelectItem>
            <SelectItem value="settings">Configurazione</SelectItem>
            <SelectItem value="user">Utenti</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm zebra min-w-[1000px]">
          <thead><tr className="text-left label-eyebrow">
            <th className="p-3">Quando</th><th className="p-3">Utente</th>
            <th className="p-3">Azione</th><th className="p-3">Entità</th>
            <th className="p-3">Note</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-neutral-500">Nessuna modifica registrata ancora.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-[#E2D9C8] cursor-pointer hover:bg-[#F3EDE1]"
                onClick={() => setSelected(r)} data-testid={`audit-${r.id}`}>
                <td className="p-3 text-xs text-neutral-600 whitespace-nowrap">{r.timestamp?.slice(0, 19).replace("T", " ")}</td>
                <td className="p-3">
                  <div className="font-medium">{r.user_email}</div>
                  <div className="text-xs text-neutral-500">{r.user_role}</div>
                </td>
                <td className="p-3">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#F3EDE1]">{ACTION_LABELS[r.action] || r.action}</span>
                </td>
                <td className="p-3 text-xs font-mono text-neutral-600">{r.entity}</td>
                <td className="p-3 text-xs text-neutral-500">{r.note || "—"}</td>
                <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  {canRestore && RESTORABLE.has(r.action) && (
                    <button data-testid={`restore-${r.id}`} onClick={() => restore(r)}
                      className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-[#E2D9C8] rounded hover:border-neutral-900 hover:bg-neutral-900 hover:text-[#FDFBF7] transition-colors mr-2">
                      <Undo2 size={11} strokeWidth={1.5} /> Ripristina
                    </button>
                  )}
                  <ChevronRight size={14} className="inline text-neutral-400" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="audit-detail">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-serif">{ACTION_LABELS[selected.action] || selected.action}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><div className="label-eyebrow">Utente</div><div className="font-medium mt-1">{selected.user_email}</div><div className="text-xs text-neutral-500">{selected.user_role}</div></div>
                  <div><div className="label-eyebrow">Timestamp</div><div className="font-mono text-xs mt-1">{selected.timestamp}</div></div>
                  <div><div className="label-eyebrow">Azione</div><div className="font-mono text-xs mt-1">{selected.action}</div></div>
                  <div><div className="label-eyebrow">Entità</div><div className="font-mono text-xs mt-1">{selected.entity || "—"} {selected.entity_id ? `(${selected.entity_id.slice(0,8)})` : ""}</div></div>
                </div>
                {selected.note && <div className="border-l-2 border-[#D7C4A3] pl-3 text-sm">{selected.note}</div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="label-eyebrow mb-2">Prima</div>
                    <pre className="text-xs bg-red-50 border border-red-200 rounded p-3 overflow-x-auto whitespace-pre-wrap">{fmtVal(selected.before)}</pre>
                  </div>
                  <div>
                    <div className="label-eyebrow mb-2">Dopo</div>
                    <pre className="text-xs bg-emerald-50 border border-emerald-200 rounded p-3 overflow-x-auto whitespace-pre-wrap">{fmtVal(selected.after)}</pre>
                  </div>
                </div>
                {canRestore && RESTORABLE.has(selected.action) && (
                  <DialogFooter>
                    <Button data-testid="restore-modal-btn" onClick={() => restore(selected)}
                      className="bg-neutral-900 hover:bg-neutral-800 text-[#FDFBF7]">
                      <Undo2 size={14} strokeWidth={1.5} /> Ripristina valore precedente
                    </Button>
                  </DialogFooter>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
